import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Check, Loader2, Flame, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BottomTabBar from "../components/BottomTabBar";

export default function Pricing() {
  const { user } = useAuth();
  const { entitlements, intro, startOrFetchIntro } = useBilling();
  const nav = useNavigate();
  const [, setCatalog] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.billingCatalog().then(setCatalog).catch(() => {});
    startOrFetchIntro();
  }, [startOrFetchIntro]);

  const buy = async (sku) => {
    if (!user) {
      toast.message("Sign in to continue");
      nav("/login?next=/pricing");
      return;
    }
    setBusy(sku);
    try {
      const { url } = await api.billingCheckout(sku);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden">
      <Header />

      {/* Ambient pink bloom behind pricing hero */}
      <div
        className="hero-bloom absolute pointer-events-none rounded-full"
        style={{
          top: 110,
          left: "50%",
          transform: "translateX(-50%)",
          width: 460,
          height: 460,
          background: "radial-gradient(circle, rgba(255, 46, 154, 0.28) 0%, transparent 70%)",
          filter: "blur(40px)",
          zIndex: 0,
        }}
        aria-hidden
      />

      <main className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-44">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-4"
            style={{
              background: "rgba(255, 46, 154, 0.08)",
              border: "1px solid rgba(255, 46, 154, 0.45)",
            }}
          >
            <Crown className="w-3 h-3 text-[#FF2E9A]" fill="#FF2E9A" />
            <span className="text-[10px] font-black tracking-[0.22em] uppercase text-[#FF2E9A]">
              Premium
            </span>
          </div>
          <h1
            className="text-white font-extrabold leading-[0.95] tracking-tight mb-4"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.4rem, 10vw, 3rem)",
              letterSpacing: "-0.04em",
            }}
            data-testid="pricing-hero-title"
          >
            Unlock the
            <br />
            <span style={{ color: "#FF2E9A", textShadow: "0 0 36px rgba(255,46,154,0.6)" }}>
              whole vibe.
            </span>
          </h1>
          <p
            className="text-[#B8B8CC]"
            style={{ fontSize: "15px", lineHeight: "1.55", fontWeight: 400 }}
          >
            All packs · unlimited plays · video responses · AI packs made for your relationship.
          </p>

          {entitlements.is_premium && (
            <div
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full"
              style={{
                background: "rgba(255, 46, 154, 0.1)",
                border: "1px solid rgba(255, 46, 154, 0.5)",
                color: "#FF2E9A",
              }}
              data-testid="pricing-current-premium"
            >
              <Crown className="w-4 h-4" fill="#FF2E9A" />
              <span className="text-[12.5px] font-bold">You're already Premium</span>
            </div>
          )}

          {!entitlements.is_premium && intro?.eligible && (
            <IntroBanner
              expiresAt={intro.expires_at}
              onClaim={() => buy("intro_spicy")}
              busy={busy === "intro_spicy"}
            />
          )}
        </motion.div>

        {/* Tiers — mobile-first stacked column */}
        <section className="space-y-3.5 mb-12" data-testid="pricing-tiers">
          <PlanCard
            sku="free"
            tag="STARTER"
            tagColor="#9B6BFF"
            accent="#9B6BFF"
            title="Free"
            price="$0"
            sub="30 plays/day · 4 free packs"
            features={[
              "Truth or Dare 🔥",
              "Date Night 🍷",
              "Late Night Drives 🌃",
              "We Listen & Don't Judge 🙊",
              "AI mix every 4th round",
            ]}
            cta="You're playing"
            disabled
          />
          <PlanCard
            sku="intro_spicy"
            tag="QUICK FIX"
            tagColor="#FFB547"
            accent="#FFB547"
            title="Spicy 24h Pass"
            price="$0.99"
            sub="Spicy Mode 🌶️ · 24 hours"
            features={[
              "Unlocks Spicy Mode 🌶️",
              "All 25 launch dares",
              "First 24 hours from purchase",
              "One-off · no auto-renew",
            ]}
            cta="Get the pass"
            onClick={() => buy("intro_spicy")}
            busy={busy === "intro_spicy"}
            disabled={
              entitlements.is_premium ||
              entitlements.has_bundle ||
              !intro?.eligible
            }
            disabledLabel={!intro?.eligible ? "Intro window closed" : null}
          />
          <PlanCard
            sku="bundle_all"
            tag="MOST POPULAR"
            tagColor="#FF2E9A"
            accent="#FF2E9A"
            title="Lifetime"
            price="$4.99"
            sub="Everything forever 😈"
            highlight
            features={[
              "All 15 packs unlocked",
              "Future packs included free",
              "Unlimited daily plays",
              "Video responses 🎥",
              "AI packs personalized to you ✨",
              "Pay once · own forever",
            ]}
            cta="Unlock everything"
            onClick={() => buy("bundle_all")}
            busy={busy === "bundle_all"}
            disabled={entitlements.is_premium || entitlements.has_bundle}
            ownedLabel="Owned"
          />
        </section>

        {/* Trust line */}
        <p
          className="text-center text-[10px] uppercase tracking-[0.3em] font-semibold"
          style={{ color: "rgba(184, 184, 204, 0.6)" }}
        >
          Secure checkout by Stripe · Pay once, own forever
        </p>
        <p
          className="text-center text-[9.5px] mt-2.5 uppercase tracking-[0.3em] font-bold"
          style={{ color: "rgba(155, 152, 174, 0.6)" }}
          data-testid="pricing-age-disclaimer"
        >
          18+ · Some packs include adult content ·{" "}
          <a href="/refund-policy" className="underline hover:text-white transition">
            Refund policy
          </a>
        </p>
      </main>
      <Footer />
      <BottomTabBar active="packs" />
    </div>
  );
}

// =====================================================
// Plan card — mobile-first, premium glass on highlight
// =====================================================
function PlanCard({
  tag,
  tagColor,
  accent,
  title,
  price,
  sub,
  highlight,
  onClick,
  busy,
  disabled,
  ownedLabel,
  features = [],
  cta,
  disabledLabel,
}) {
  const isOwned = disabled && (ownedLabel || disabledLabel);
  return (
    <motion.button
      onClick={onClick}
      disabled={busy || disabled}
      whileHover={{ y: disabled ? 0 : -3 }}
      transition={{ duration: 0.25 }}
      className={`relative w-full text-left rounded-[26px] p-5 overflow-hidden disabled:opacity-70 flex flex-col ${
        highlight ? "premium-sweep" : ""
      }`}
      style={
        highlight
          ? {
              background:
                "linear-gradient(135deg, #FF2E9A 0%, #FF4FBF 38%, #B91C84 100%)",
              border: "1.5px solid rgba(255, 255, 255, 0.18)",
              boxShadow:
                "0 0 36px rgba(255, 46, 154, 0.55), 0 18px 50px rgba(255, 46, 154, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.18)",
            }
          : {
              background: "linear-gradient(180deg, rgba(20, 14, 26, 0.85) 0%, rgba(12, 8, 18, 0.9) 100%)",
              border: `1px solid ${accent}33`,
              boxShadow: `0 8px 28px rgba(0, 0, 0, 0.35), 0 0 18px ${accent}1a`,
            }
      }
      data-testid={`pricing-${title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}-button`}
    >
      {/* Tag chip */}
      <div className="flex items-center justify-between mb-3 relative z-[2]">
        <span
          className="text-[10px] font-black uppercase px-2.5 py-1 rounded-md tracking-[0.13em]"
          style={
            highlight
              ? {
                  background: "rgba(0, 0, 0, 0.4)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                }
              : {
                  background: `${tagColor}1f`,
                  color: tagColor,
                  border: `1px solid ${tagColor}55`,
                }
          }
        >
          {tag}
        </span>
        {highlight && (
          <Crown
            className="w-4 h-4 text-white"
            fill="white"
            style={{ filter: "drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))" }}
          />
        )}
      </div>

      {/* Price block */}
      <div className="relative z-[2] mb-4">
        <div
          className={`font-extrabold leading-none tracking-tight ${highlight ? "text-white" : "text-white"}`}
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "44px",
            letterSpacing: "-0.04em",
            textShadow: highlight ? "0 2px 12px rgba(0, 0, 0, 0.35)" : undefined,
          }}
        >
          {price}
        </div>
        <div
          className={`font-bold mt-1.5 ${highlight ? "text-white" : "text-white"}`}
          style={{ fontSize: "17px", fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </div>
        <div
          className={`mt-0.5 ${highlight ? "text-white/85" : "text-[#9090A8]"}`}
          style={{ fontSize: "12px", lineHeight: "1.5", fontWeight: 400 }}
        >
          {sub}
        </div>
      </div>

      {/* Features */}
      {features.length > 0 && (
        <ul className="space-y-2 mb-5 relative z-[2]" aria-label={`${title} features`}>
          {features.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-2"
              style={{
                fontSize: "13px",
                lineHeight: "1.5",
                color: highlight ? "rgba(255, 255, 255, 0.95)" : "#C9C7D9",
                fontWeight: 400,
              }}
            >
              <Check
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                strokeWidth={3}
                style={{ color: highlight ? "#fff" : accent }}
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA */}
      <span
        className="mt-auto inline-flex items-center justify-center gap-2 rounded-full py-3 text-[13px] font-extrabold relative z-[2]"
        style={
          isOwned
            ? {
                background: "rgba(255, 255, 255, 0.07)",
                color: "rgba(255, 255, 255, 0.55)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }
            : highlight
            ? {
                background: "rgba(0, 0, 0, 0.4)",
                color: "#fff",
                border: "1px solid rgba(255, 255, 255, 0.25)",
                backdropFilter: "blur(4px)",
              }
            : {
                background: `linear-gradient(135deg, ${accent}, ${accent}D0)`,
                color: tagColor === "#FFB547" ? "#1a0e00" : "#fff",
                boxShadow: `0 0 18px ${accent}90`,
              }
        }
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : !isOwned && !highlight && tagColor !== "#9B6BFF" ? (
          <Flame className="w-4 h-4" />
        ) : !isOwned && highlight ? (
          <Sparkles className="w-4 h-4" />
        ) : null}
        {busy ? "Opening…" : isOwned ? (ownedLabel || disabledLabel || "Active") : cta}
        {!busy && !isOwned && <ChevronRight className="w-3.5 h-3.5" strokeWidth={3} />}
      </span>
    </motion.button>
  );
}

// =====================================================
// Intro banner — first-open $0.99 spicy 24h
// =====================================================
function IntroBanner({ expiresAt, onClaim, busy }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  if (ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="premium-sweep mt-6 relative overflow-hidden rounded-[22px] p-4 flex items-center gap-3"
      style={{
        background: "linear-gradient(135deg, rgba(255, 46, 154, 0.18), rgba(123, 97, 255, 0.18)), rgba(15, 8, 18, 0.85)",
        border: "1.5px solid rgba(255, 46, 154, 0.6)",
        boxShadow: "0 0 30px rgba(255, 46, 154, 0.35)",
      }}
      data-testid="pricing-intro-banner"
    >
      <motion.span
        animate={{ rotate: [0, 8, -6, 0], scale: [1, 1.12, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="text-3xl flex-shrink-0"
      >
        🌶️
      </motion.span>
      <div className="flex-1 relative min-w-0">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FF2E9A] mb-0.5">
          First-open special · 24h
        </p>
        <p
          className="text-white font-extrabold leading-tight"
          style={{ fontSize: "16px", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}
        >
          Spicy Mode for{" "}
          <span style={{ color: "#FF2E9A" }}>$0.99</span>
          <span className="text-[#9090A8] text-[12px] font-bold line-through ml-1.5">$1.99</span>
        </p>
        <p className="text-[11px] text-[#B8B8CC] mt-0.5">
          Ends in{" "}
          <span
            className="font-mono tabular-nums text-[#FF2E9A] font-bold"
            data-testid="pricing-intro-countdown"
          >
            {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
          </span>
        </p>
      </div>
      <motion.button
        onClick={onClaim}
        disabled={busy}
        whileTap={{ scale: 0.95 }}
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="relative rounded-full px-4 py-2.5 font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-60 flex-shrink-0"
        style={{
          background: "linear-gradient(135deg, #FF2E9A, #E63A78)",
          boxShadow: "0 0 18px rgba(255, 46, 154, 0.6)",
          fontSize: "12.5px",
        }}
        data-testid="pricing-intro-claim"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flame className="w-3.5 h-3.5" />}
        {busy ? "…" : "Claim"}
      </motion.button>
    </motion.div>
  );
}
